import { useMemo } from "react";
import { AxiosError } from "axios";

import { OperationsDataEnvelope } from "../../../models/operations";
import {
  QueryStatus,
  QueryEnvelope,
  EnsuredQueryEnvelope,
  ensureState,
  ensureStateWithData,
} from "../../../services/query";

import { operationsAccessError } from "./preOperations";

import { useOperationsQuery } from "./operations.query";
import {
  useOperationsGlobalActivity,
  useOperationsGlobalStatus,
} from "./operations.globalStatus";

export { useOperationsGlobalActivity, useOperationsGlobalStatus };

export * from "./operations.invalidate";
export * from "./operations.mutations";

// #########################
// ## useOperationsStatus ##
// #########################

export type ResultOfUseOperationsStatus = QueryStatus;
export type UseOperationsStatus = () => ResultOfUseOperationsStatus;

/**
 * The returned value of this hook will contain the status
 * of the fetching of the operations data.
 */
export const useOperationsStatus: UseOperationsStatus = () => {
  return useOperationsQuery({ watch: ["status"] }).status;
};

// ########################
// ## useOperationsError ##
// ########################

export type ResultOfUseOperationsError = AxiosError | null;
export type UseOperationsError = () => ResultOfUseOperationsError;

/**
 * The returned value of this hook will contain the error
 * that has occurred in the fetching of the operations data
 * or null if no error occurred.
 */
export const useOperationsError: UseOperationsError = () => {
  return useOperationsQuery({ watch: ["error"] }).error;
};

// ############################
// ## useMaybeOperationsData ##
// ############################

export type ResultOfUseMaybeOperationsData = OperationsDataEnvelope | undefined;
export type UseMaybeOperationsData = () => ResultOfUseMaybeOperationsData;

/**
 * The returned value of this hook will contain
 * the operations data, which may be undefined
 */
export const useMaybeOperationsData: UseMaybeOperationsData = () => {
  return useOperationsQuery({ watch: ["data"] }).data;
};

// #######################
// ## useOperationsData ##
// #######################

export type ResultOfUseOperationsData = OperationsDataEnvelope;
export type UseOperationsData = () => ResultOfUseOperationsData;

/**
 * The returned value of this hook will contain the operations data.
 *
 * This hook is guaranteed to contain operations data or will throw.
 */
export const useOperationsData: UseOperationsData = () => {
  const data = useMaybeOperationsData();
  if (data === undefined) {
    throw operationsAccessError;
  }
  return data;
};

// ########################
// ## useMaybeOperations ##
// ########################

export type ResultOfUseMaybeOperations = QueryEnvelope<OperationsDataEnvelope>;
export type UseMaybeOperations = () => ResultOfUseMaybeOperations;

/**
 * The `data` property returned from this hook will contain
 * the operations data, which may be undefined
 *
 * The `error` and `status` properties returned from this hook
 * will reflect the state of the operations data.
 */
export const useMaybeOperations: UseMaybeOperations = () => {
  const { status, error, data } = useOperationsQuery({
    watch: ["status", "error", "data"],
  });
  return useMemo(
    () => ensureState({ status, error, data }),
    [status, error, data],
  );
};

// ###################
// ## useOperations ##
// ###################

export type ResultOfUseOperations =
  EnsuredQueryEnvelope<OperationsDataEnvelope>;
export type UseOperations = () => ResultOfUseOperations;

/**
 * The `data` property returned from this hook will contain
 * the operations data.
 *
 * The `error` and `status` properties returned from this hook
 * will reflect the state of the operations data.
 *
 * This hook is guaranteed to contain operations data or will throw.
 */
export const useOperations: UseOperations = () => {
  const { status, error, data } = useOperationsQuery({
    watch: ["status", "error", "data"],
  });
  if (data === undefined) {
    throw operationsAccessError;
  }
  return useMemo(
    () => ensureStateWithData({ status, error, data }),
    [status, error, data],
  );
};
